/**
 * Per-iter-61/62 helper: build a small noise CanvasTexture suitable
 * for multiplying into a board surface to produce a visible grainy
 * wood-grain sheen.  Cached per (key, repeat, depth).
 *
 * The texture's pixels hover near 1.0 (white) with a downward jitter
 * (alpha-dark grains) — when applied as `material.map` while leaving
 * `material.color` set to the desired wood colour, the multiplicative
 * blend produces colour × (1-depth..1.0) noise — visibly grainy
 * without shifting the underlying hue.
 *
 * Per user iter 62: depth bumped considerably to make the grain
 * obviously visible at the camera distances used by the chess and
 * checkers boards.
 */
import * as THREE from "three";

const _grainCache = new Map();

export function makeGrainTexture(key = "default", repeat = 4, depth = 0.45) {
  const cacheKey = `${key}-${repeat}-${depth}`;
  if (_grainCache.has(cacheKey)) return _grainCache.get(cacheKey);
  const W = 256, H = 256;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  const img = ctx.createImageData(W, H);
  for (let i = 0; i < W * H; i++) {
    const px = i % W;
    const py = Math.floor(i / W);
    // Two independent noise sources + a horizontal-streak component
    // keyed on row index to suggest plank grain.
    const n1 = Math.random();
    const n2 = Math.random();
    // Streak contour: thin dark bands every ~12-22 px in y.
    const bandPhase = Math.sin(py * 0.18 + Math.sin(px * 0.012) * 1.3);
    const streakDip = bandPhase < -0.55 ? (1 + bandPhase) : 1.0;  // 0..1 dip on bands
    // Combine: high-frequency speckle + occasional dark band.
    const speckle = n1 * 0.55 + n2 * 0.20;
    const v = 1.0 - depth * (speckle + (1 - streakDip) * 0.75);
    const c = Math.max(0, Math.min(255, Math.floor(v * 255)));
    const o = i * 4;
    img.data[o] = c;
    img.data[o + 1] = c;
    img.data[o + 2] = c;
    img.data[o + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  _grainCache.set(cacheKey, tex);
  return tex;
}
